import React, {FC, useCallback, useContext} from 'react';
import styled from 'styled-components';

import {supportsPopout} from '../../../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  LoadedMessageModel,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {ReplyLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownSeparator} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {NewFeaturePill} from '../../../../../../libs/shared/ui/src/components/pill/featurePill';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useClipboard} from '../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ThemeControllerContext} from '../../../../../../libs/shared/ui/src/theme/themeControllerContext';
import {PermissionResult} from '../../../../../../src/helpers/permissionHelpers';
import {
  buildCopyLinkDataTestId,
  generateMessagePermalink,
} from '../../../../../../srcCommon/helpers/premalinksHelpers';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useDestroyMessageMessageSendingPermission} from '../../../../../../srcCommon/permissions/useMessageSendingPermission';
import {
  useComposeMessageOfflinePermission,
  useConversationSeenActionOfflinePermission,
} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {buildMessagePublicId} from '../../../../../helpers/models/messageHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {LegacyMessageMoreDropdownEntryPointModel} from '../../../../../models/api/legacyEntryPointModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {makeGetLegacyApplicationFromEntryPoint} from '../../../../../store/data/applications/legacyApplicationsSelectors';
import {getCompanyPreferences} from '../../../../../store/data/company/companySelectors';
import {PermissionDropdownItem} from '../../../../common/dropdowns/permissionDropdownItem';
import {PermalinkPermissionTooltip} from '../../conversation/actions/more/permalinkPermissionTooltip';
import {ThemeControllerDropdownItem} from './messageThemeController';

/*
 * Props.
 */

export type ForwardHandler = (location: ReplyLocationsEnum, shouldCreateConversation: boolean) => void;
export type CloneHandler = (location: ReplyLocationsEnum, shouldCreateConversation: boolean) => void;

interface MessageViewerMoreDropdownProps extends PopoverTransferProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | ContactDetailsPanelId | AccountDetailsPanelId;
  namespaces: string[];
  host: string;
  cellsContext: CellsContext;
  message: LoadedMessageModel;
  entryPoints: ReadonlyArray<LegacyMessageMoreDropdownEntryPointModel>;
  canSplit: boolean;
  conversation: ConversationModel | undefined;
  showReplyAction: boolean;
  isInTeammateContext: boolean;
  hasNewVisitorUrlExperience?: boolean;
  canLinkConversation?: boolean;
  onReplyClick: (arg: ReplyLocationsEnum) => void;
  onReplyAllClick: (arg: ReplyLocationsEnum) => void;
  onForwardClick: ForwardHandler;
  onCloneClick: CloneHandler;
  onPrintClick: () => void;
  onSaveEmailClick: () => void;
  onViewSourceClick: () => void;
  onPopoutClick: () => void;
  onSplitClick: () => void;
  onMarkUnreadClick: () => void;
  onDestroyClick: () => void;
  onEntryPointSelect: (model: LegacyMessageMoreDropdownEntryPointModel) => void;
  openInBrowser: (arg: string) => void;
  onPopoutLinkedConversationClick: () => void;
}

/*
 * Styles.
 */

const StyledDropdownItemContentWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledLearnMoreSpan = styled.span`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  line-height: 1.5;
  text-decoration-line: underline;
`;

/*
 * Component.
 */

export const MessageViewerMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  switch (props.message.type) {
    case MessageTypesEnum.EMAIL:
      return <EmailMoreDropdown {...props} />;

    case MessageTypesEnum.TWEET:
      return <TweetMoreDropdown {...props} />;

    case MessageTypesEnum.TWEET_DM:
      return <TweetDmMoreDropdown {...props} />;

    case MessageTypesEnum.SMS:
      return <SmsMoreDropdown {...props} />;

    case MessageTypesEnum.FACEBOOK:
      return <FacebookMoreDropdown {...props} />;

    case MessageTypesEnum.INTERCOM:
      return <IntercomMoreDropdown {...props} />;

    case MessageTypesEnum.FRONT_CHAT:
    case MessageTypesEnum.LAYER:
      return <LayerMoreDropdown {...props} />;

    case MessageTypesEnum.CUSTOM:
      return <CustomMoreDropdown {...props} />;

    default:
      return <OtherMoreDropdown {...props} />;
  }
};

const EmailMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {canLinkConversation, showReplyAction} = props;

  const canPopout =
    [MessageStatusesEnum.SENT, MessageStatusesEnum.IMPORTED].includes(props.message.status) &&
    RViewerPanelId.is(props.panelId) &&
    supportsPopout();

  const messageComposeOfflinePermission = useComposeMessageOfflinePermission();
  const isEMLFileViewer = props.message.visualStatus === MessageVisualStatusesEnum.EML_FILE;
  const messageThemeContext = useContext(ThemeControllerContext);

  return (
    <SimpleDropdown {...props} placement="bottom-end">
      {showReplyAction && <ReplyDropdownItems {...props} />}
      <PermissionCheck offlinePermission={messageComposeOfflinePermission}>
        {(permission) => (
          <>
            <ForwardDropdownItems
              canSendEmails={permission.isAllowed}
              onForwardClick={props.onForwardClick}
              isEMLFileViewer={isEMLFileViewer}
            />
            {!isEMLFileViewer && (
              <CloneDropdownItems canSendEmails={permission.isAllowed} onCloneClick={props.onCloneClick} />
            )}
          </>
        )}
      </PermissionCheck>
      {canLinkConversation && <LinkedConversationDropdownItems {...props} />}
      {!isEMLFileViewer && <SplitDropdownItem {...props} />}
      <DropdownSeparator />

      {!isEMLFileViewer && <MarkUnreadDropdownItem {...props} />}
      {!isEMLFileViewer && <DestroyDropdownItem {...props} />}
      <DropdownItem onSelect={props.onPrintClick}>
        <DropdownItemIcon
          label={<Trans id="message-actions-print">Print</Trans>}
          iconSize={16}
          iconName="printSmall"
        />
        <DropdownItemContent>
          <Trans id="message-actions-print">Print</Trans>
        </DropdownItemContent>
      </DropdownItem>
      {!isEMLFileViewer && (
        <>
          {props.message.type === MessageTypesEnum.EMAIL && (
            <DropdownItem onSelect={props.onSaveEmailClick}>
              <DropdownItemIcon
                label={<Trans id="message-actions-save">Download</Trans>}
                iconSize={16}
                iconName="downloadSmall"
              />
              <DropdownItemContent>
                <Trans id="message-actions-save">Download</Trans>
              </DropdownItemContent>
            </DropdownItem>
          )}
          <DropdownSeparator />
          <EntryPointDropdownItems {...props} />
          <CopyDropdownItems {...props} />
          <DropdownItem onSelect={props.onViewSourceClick}>
            <DropdownItemIcon
              label={<Trans id="message-actions-viewsource">View source</Trans>}
              iconSize={16}
              iconName="codeSmall"
            />
            <DropdownItemContent>
              <Trans id="message-actions-viewsource">View source</Trans>
            </DropdownItemContent>
          </DropdownItem>
          {canPopout && (
            <DropdownItem onSelect={props.onPopoutClick}>
              <DropdownItemIcon
                label={<Trans id="message-actions-popout">View in external window</Trans>}
                iconSize={16}
                iconName="externalLinkSmall"
              />
              <DropdownItemContent>
                <Trans id="message-actions-popout">View in external window</Trans>
              </DropdownItemContent>
            </DropdownItem>
          )}
          {
            <ThemeControllerDropdownItem
              shouldRenderThemeOverrideUi={messageThemeContext.shouldRenderThemeOverrideUi}
              isLightThemeOverride={messageThemeContext.isLightThemeOverride}
              onToggleLightThemeOverride={messageThemeContext.onToggleLightThemeOverride}
            />
          }
        </>
      )}
    </SimpleDropdown>
  );
};

const TweetMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser, showReplyAction, canLinkConversation} = props;
  const twitterExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterExtra;
  const twitterMessage = <Trans id="message-actions-viewontwitter">View on Twitter</Trans>;

  return (
    <SimpleDropdown {...props}>
      {showReplyAction && <ReplyDropdownItems {...props} />}
      <MarkUnreadDropdownItem {...props} />
      {twitterExtra && (
        <DropdownItem onSelect={() => openInBrowser(twitterExtra.tweetUrl)}>
          <DropdownItemIcon
            label={twitterMessage}
            iconSize={16}
            iconName="twitterColoredSmall"
            shouldRespectFill
          />
          <DropdownItemContent>{twitterMessage}</DropdownItemContent>
        </DropdownItem>
      )}
      {canLinkConversation && <LinkedConversationDropdownItems {...props} />}
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const TweetDmMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser, showReplyAction, canLinkConversation} = props;
  const twitterDmExtra =
    message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterDmExtra;
  const twitterMessage = <Trans id="message-actions-viewontwitter">View on Twitter</Trans>;

  return (
    <SimpleDropdown {...props}>
      {showReplyAction && <ReplyDropdownItems {...props} />}
      <MarkUnreadDropdownItem {...props} />
      {twitterDmExtra && (
        <DropdownItem onSelect={() => openInBrowser(twitterDmExtra.dmUrl)}>
          <DropdownItemIcon
            label={twitterMessage}
            iconSize={16}
            iconName="twitterColoredSmall"
            shouldRespectFill
          />
          <DropdownItemContent>{twitterMessage}</DropdownItemContent>
        </DropdownItem>
      )}
      {canLinkConversation && <LinkedConversationDropdownItems {...props} />}
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const SmsMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    {props.showReplyAction && <ReplyDropdownItem {...props} />}
    <MarkUnreadDropdownItem {...props} />
    {props.canLinkConversation && <LinkedConversationDropdownItems {...props} />}
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const FacebookMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    {props.showReplyAction && <ReplyDropdownItem {...props} />}
    <MarkUnreadDropdownItem {...props} />
    {props.canLinkConversation && <LinkedConversationDropdownItems {...props} />}
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const CustomMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    {props.showReplyAction && <ReplyDropdownItem {...props} />}
    <MarkUnreadDropdownItem {...props} />
    {props.canLinkConversation && <LinkedConversationDropdownItems {...props} />}
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const IntercomMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, openInBrowser, showReplyAction, canLinkConversation} = props;
  const intercomExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.intercomExtra;
  const intercomText = <Trans id="message-actions-viewonintercom">View on Intercom</Trans>;

  return (
    <SimpleDropdown {...props}>
      {showReplyAction && <ReplyDropdownItem {...props} />}
      <MarkUnreadDropdownItem {...props} />
      {intercomExtra && (
        <DropdownItem onSelect={() => openInBrowser(intercomExtra.intercomUrl)}>
          <DropdownItemIcon
            label={intercomText}
            iconSize={16}
            iconName="intercomColoredSmall"
            shouldRespectFill
          />
          <DropdownItemContent>{intercomText}</DropdownItemContent>
        </DropdownItem>
      )}
      {canLinkConversation && <LinkedConversationDropdownItems {...props} />}
      <SplitDropdownItem {...props} />
      <DestroyDropdownItem {...props} />
      <DropdownSeparator />
      <EntryPointDropdownItems {...props} />
      <CopyDropdownItems {...props} />
    </SimpleDropdown>
  );
};

const LayerMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <MarkUnreadDropdownItem {...props} />
    {props.canLinkConversation && <LinkedConversationDropdownItems {...props} />}
    <SplitDropdownItem {...props} />
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

const OtherMoreDropdown: FC<MessageViewerMoreDropdownProps> = (props) => (
  <SimpleDropdown {...props}>
    <MarkUnreadDropdownItem {...props} />
    <SplitDropdownItem {...props} />
    {props.canLinkConversation && <LinkedConversationDropdownItems {...props} />}
    <DestroyDropdownItem {...props} />
    <DropdownSeparator />
    <EntryPointDropdownItems {...props} />
    <CopyDropdownItems {...props} />
  </SimpleDropdown>
);

/*
 * Various actions.
 */

const ReplyDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onReplyClick} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) => (
        <DropdownItem
          onSelect={() => onReplyClick(ReplyLocationsEnum.THREE_DOTS)}
          isDisabled={!permission.isAllowed}
        >
          <DropdownItemIcon
            label={<Trans id="message-actions-reply">Reply</Trans>}
            iconSize={16}
            iconName="replySmall"
          />
          <DropdownItemContent>
            <Trans id="message-actions-reply">Reply</Trans>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

const ReplyDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onReplyClick, onReplyAllClick, showReplyAction} = props;
  const offlinePermission = useComposeMessageOfflinePermission();

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) => (
        <>
          <DropdownItem
            onSelect={() => onReplyClick(ReplyLocationsEnum.THREE_DOTS)}
            isDisabled={!permission.isAllowed || !showReplyAction}
          >
            <DropdownItemIcon
              label={<Trans id="message-actions-reply">Reply</Trans>}
              iconSize={16}
              iconName="replySmall"
            />
            <DropdownItemContent>
              <Trans id="message-actions-reply">Reply</Trans>
            </DropdownItemContent>
          </DropdownItem>
          <DropdownItem
            onSelect={() => onReplyAllClick(ReplyLocationsEnum.THREE_DOTS)}
            isDisabled={!permission.isAllowed || !showReplyAction}
          >
            <DropdownItemIcon
              label={<Trans id="message-actions-replyall">Reply all</Trans>}
              iconSize={16}
              iconName="replyAll"
            />
            <DropdownItemContent>
              <Trans id="message-actions-replyall">Reply all</Trans>
            </DropdownItemContent>
          </DropdownItem>
        </>
      )}
    </PermissionCheck>
  );
};

interface ForwardDropdownItemsProps {
  canSendEmails: boolean;
  onForwardClick: ForwardHandler;
  isEMLFileViewer: boolean;
}

const ForwardDropdownItems: FC<ForwardDropdownItemsProps> = (props) => {
  /* You can forward a message even if you don't have the permission to reply. */
  const {canSendEmails, onForwardClick} = props;
  const offlinePermission = useComposeMessageOfflinePermission();
  const forwardText = <Trans id="conversation-actions-forward">Forward</Trans>;

  return (
    <>
      <PermissionDropdownItem
        offlinePermission={offlinePermission}
        isDisabled={!canSendEmails}
        onSelect={() => {
          onForwardClick(ReplyLocationsEnum.THREE_DOTS, false);
        }}
      >
        <DropdownItemIcon label={forwardText} iconSize={16} iconName="forwardSmall" />
        <DropdownItemContent>{forwardText}</DropdownItemContent>
      </PermissionDropdownItem>
      <PermissionDropdownItem
        offlinePermission={offlinePermission}
        isDisabled={!canSendEmails}
        onSelect={() => {
          onForwardClick(ReplyLocationsEnum.THREE_DOTS, true);
        }}
      >
        <DropdownItemIcon label={forwardText} iconSize={16} iconName="forwardAsNewSmall" />
        <DropdownItemContent>
          <Trans id="forward-new-conversation-title">Forward as a new conversation</Trans>
        </DropdownItemContent>
      </PermissionDropdownItem>
    </>
  );
};

interface CloneDropdownItemsProps {
  canSendEmails: boolean;
  onCloneClick: CloneHandler;
}

const CloneDropdownItems: FC<CloneDropdownItemsProps> = (props) => {
  /* You can resend a message even if you don't have the permission to reply. */
  const {canSendEmails, onCloneClick} = props;
  const offlinePermission = useComposeMessageOfflinePermission();
  const resendText = <Trans id="message-actions-resend">Resend</Trans>;

  return (
    <>
      <PermissionDropdownItem
        offlinePermission={offlinePermission}
        isDisabled={!canSendEmails}
        onSelect={() => onCloneClick(ReplyLocationsEnum.THREE_DOTS, false)}
      >
        <DropdownItemIcon label={resendText} iconSize={16} iconName="sendSmall" />
        <DropdownItemContent>{resendText}</DropdownItemContent>
      </PermissionDropdownItem>
      <PermissionDropdownItem
        offlinePermission={offlinePermission}
        isDisabled={!canSendEmails}
        onSelect={() => onCloneClick(ReplyLocationsEnum.THREE_DOTS, true)}
      >
        <DropdownItemIcon label={resendText} iconSize={16} iconName="resendAsNewSmall" />
        <DropdownItemContent>
          <Trans id="resend-new-conversation-title">Resend as a new conversation</Trans>
        </DropdownItemContent>
      </PermissionDropdownItem>
    </>
  );
};

const SplitDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {message, canSplit, onSplitClick, conversation} = props;
  const {navigateToLink} = useLinkNavigation();

  const renderSplitErrorTooltip = useCallback(() => {
    if (!props.canSplit && conversation) {
      if (conversation.lastDraft) {
        return (
          <Trans id="message-actions-splitconversation-draft-error">
            Cannot split conversation when there is an active draft.
          </Trans>
        );
      }
      if (message.isFirstMessage) {
        return (
          <Trans id="message-actions-splitconversation-firstmessage-error">
            Cannot split conversation on the first message.
          </Trans>
        );
      }
      // TODO: We should add the other cases as well, need to work with product/design for copies
    }

    return null;
  }, [conversation, props.canSplit]);

  if (message.type === MessageTypesEnum.EMAIL) {
    const moveConversationText = (
      <Trans id="message-actions-splitconversation-email">Move to a new conversation</Trans>
    );

    return (
      <PermissionDropdownItem
        onSelect={onSplitClick}
        isDisabled={!canSplit}
        renderTooltip={renderSplitErrorTooltip}
        tooltipMaxWidth={224}
      >
        <DropdownItemIcon label={moveConversationText} iconSize={16} iconName="moveRightSmall" />
        <DropdownItemContent>{moveConversationText}</DropdownItemContent>
      </PermissionDropdownItem>
    );
  }

  const splitConversationText = (
    <Trans id="message-actions-splitconversation-other">Split conversation</Trans>
  );

  return (
    <PermissionDropdownItem
      onSelect={onSplitClick}
      isDisabled={!canSplit}
      renderTooltip={renderSplitErrorTooltip}
    >
      <DropdownItemIcon label={splitConversationText} iconSize={16} iconName="splitSmall" shouldRespectFill />
      <DropdownItemContent>
        <StyledDropdownItemContentWrapperDiv>
          {splitConversationText}
          <TooltipCoordinator
            render={() => (
              <>
                <Trans id="new-feature-split-conversation">
                  Splitting will create a new conversation for this message and all subsequent activity.
                  Splitting can be automated in channel settings.{' '}
                </Trans>
                <StyledLearnMoreSpan
                  onClick={() => navigateToLink('https://help.front.com/en/articles/2307')}
                >
                  <Trans id="split-conversation-learn-more">Learn more</Trans>
                </StyledLearnMoreSpan>
              </>
            )}
            placement="bottom"
            maxWidth={240}
            persistent
            isMultiline
          >
            <NewFeaturePill />
          </TooltipCoordinator>
        </StyledDropdownItemContentWrapperDiv>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

const MarkUnreadDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {onMarkUnreadClick} = props;
  const offlinePermission = useConversationSeenActionOfflinePermission();
  const markUnreadText = <Trans id="message-actions-markmessageunread">Mark as unread</Trans>;
  return (
    <PermissionDropdownItem onSelect={onMarkUnreadClick} offlinePermission={offlinePermission}>
      <DropdownItemIcon label={markUnreadText} iconSize={16} iconName="messageSmall" />
      <DropdownItemContent>{markUnreadText}</DropdownItemContent>
    </PermissionDropdownItem>
  );
};

const DestroyDropdownItem: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {namespaces, onDestroyClick, message} = props;
  const messageSendingPermission = useDestroyMessageMessageSendingPermission(message);
  const makeRenderTooltip = (permission: PermissionResult) =>
    !permission.isAllowed ? () => <PermissionDenied reason={permission.reason} /> : undefined;
  const deleteText = <Trans id="message-actions-deletepermanently">Delete</Trans>;

  return (
    <PermissionCheck
      namespaces={namespaces}
      permission={PermissionsEnum.CONVERSATION_DESTROY}
      messageSendingPermission={messageSendingPermission}
    >
      {(permission) => (
        <DropdownItem
          renderTooltip={makeRenderTooltip(permission)}
          onSelect={onDestroyClick}
          isDisabled={!permission.isAllowed}
        >
          <DropdownItemIcon label={deleteText} iconSize={16} iconName="trashSmall" />
          <DropdownItemContent>{deleteText}</DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

const EntryPointDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {entryPoints, onEntryPointSelect} = props;
  return (
    <>
      {entryPoints.map((entryPoint) => (
        <DropdownItem key={entryPoint.uid} onSelect={() => onEntryPointSelect(entryPoint)}>
          <EntryPointContentRenderer entryPoint={entryPoint} />
        </DropdownItem>
      ))}
    </>
  );
};

const EntryPointContentRenderer: FC<{entryPoint: LegacyMessageMoreDropdownEntryPointModel}> = ({
  entryPoint,
}) => {
  const application = useAppSelector(makeGetLegacyApplicationFromEntryPoint<void>(() => entryPoint));

  return (
    <>
      {application.typeId === 'slack' && (
        <DropdownItemIcon
          label={entryPoint.name}
          iconSize={16}
          iconName="slackColoredSmall"
          shouldRespectFill
        />
      )}
      <DropdownItemContent>{entryPoint.name}</DropdownItemContent>
    </>
  );
};

const CopyDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const {copy} = useClipboard();
  const {host, message} = props;
  const preferences = useAppSelector(getCompanyPreferences);
  const twitterExtra = message.visualStatus === MessageVisualStatusesEnum.TIMELINE && message.twitterExtra;

  /*
   * Event handlers.
   */

  const onCopyMessageIdClick = useCallback(() => {
    copy(buildMessagePublicId(message));
  }, [copy, message]);

  const onCopyMessageLinkClick = useCallback(async () => {
    copy(generateMessagePermalink(message, host, preferences));
  }, [copy, host, message, preferences]);

  const onCopyTweetLinkClick = useCallback(() => {
    if (twitterExtra) {
      copy(twitterExtra.tweetUrl);
    }
  }, [copy, twitterExtra]);

  /*
   * Render.
   */

  return (
    <>
      <DropdownItem onSelect={onCopyMessageIdClick}>
        <DropdownItemIcon
          label={<Trans id="message-actions-copyid">Copy message ID</Trans>}
          iconSize={16}
          iconName="copySmall"
        />
        <DropdownItemContent>
          <Trans id="message-actions-copyid">Copy message ID</Trans>
        </DropdownItemContent>
      </DropdownItem>
      <DropdownItem
        onSelect={onCopyMessageLinkClick}
        interactionId={buildCopyLinkDataTestId('message', preferences)}
      >
        <DropdownItemIcon
          label={<Trans id="message-actions-copylink">Copy message link</Trans>}
          iconSize={16}
          iconName="linkSmall"
        />
        <DropdownItemContent renderTooltip={() => <PermalinkPermissionTooltip preferences={preferences} />}>
          <Trans id="message-actions-copylink">Copy message link</Trans>
        </DropdownItemContent>
      </DropdownItem>
      {twitterExtra && (
        <DropdownItem onSelect={onCopyTweetLinkClick}>
          <DropdownItemIcon
            label={<Trans id="message-actions-copytweetlink">Copy link to Tweet</Trans>}
            iconSize={16}
            iconName="linkSmall"
          />
          <DropdownItemContent>
            <Trans id="message-actions-copytweetlink">Copy link to Tweet</Trans>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </>
  );
};

const LinkedConversationDropdownItems: FC<MessageViewerMoreDropdownProps> = (props) => {
  const linkedConversationText = (
    <Trans id="message-actions-linked-conversation">Create linked conversation</Trans>
  );

  return (
    <DropdownItem onSelect={props.onPopoutLinkedConversationClick}>
      <DropdownItemIcon label={linkedConversationText} iconSize={16} iconName="linkedConversationsSmall" />
      <DropdownItemContent>
        <StyledDropdownItemContentWrapperDiv>{linkedConversationText}</StyledDropdownItemContentWrapperDiv>
      </DropdownItemContent>
    </DropdownItem>
  );
};
